import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Download } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { ProductionUnitPickerModal } from "../components/ProductionTrackingModal/ProductionUnitPickerModal";
import { ProductionTrackingModal } from "../components/ProductionTrackingModal/ProductionTrackingModal";
import { useOrderList, useRetryOrderSync } from "../hooks/use-orders";
import { useAssignableUsers } from "../hooks/use-companies";
import { useAuth } from "../lib/use-auth";
import { formatDate, formatAmount } from "../lib/date";
import { apiUrl } from "../lib/api";
import styles from "./Orders.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/orders",
	component: withPermission(OrdersPage, "orders", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
		assignedTo: search.assignedTo ? String(search.assignedTo) : undefined,
		orderDateFrom: String(search.orderDateFrom || ""),
		orderDateTo: String(search.orderDateTo || ""),
	}),
});

function OrdersPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const searchParams = route.useSearch();
	const status = searchParams.status;
	const page = searchParams.page;
	const pageSize = searchParams.size;
	const searchQuery = searchParams.search;
	const assignedTo = searchParams.assignedTo;
	const orderDateFrom = searchParams.orderDateFrom;
	const orderDateTo = searchParams.orderDateTo;
	const { user } = useAuth();
	const isAdmin = user?.role === "admin";
	const { data: assignableUsers } = useAssignableUsers({ enabled: isAdmin });
	const { data, isLoading, isError, refetch } = useOrderList({
		status: status || undefined,
		search: searchQuery || undefined,
		assignedTo: assignedTo || undefined,
		orderDateFrom: orderDateFrom || undefined,
		orderDateTo: orderDateTo || undefined,
		page,
		limit: pageSize,
	});

	const orders = data?.items ?? [];
	const total = data?.total ?? 0;
	const retrySync = useRetryOrderSync();
	const [pickerOrderId, setPickerOrderId] = useState<string | null>(null);
	const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
	const [detailSerial, setDetailSerial] = useState<string | null>(null);

	const statusOptions = [
		{ value: "", label: t("all") },
		{ value: "open", label: "Açık" },
		{ value: "closed", label: "Kapalı" },
		{ value: "cancelled", label: "İptal Edildi" },
	];

	const handleExport = () => {
		const params = new URLSearchParams();
		if (status) params.set("status", status);
		if (searchQuery) params.set("search", searchQuery);
		if (assignedTo) params.set("assignedTo", assignedTo);
		if (orderDateFrom) params.set("orderDateFrom", orderDateFrom);
		if (orderDateTo) params.set("orderDateTo", orderDateTo);
		window.open(apiUrl(`/api/orders/export?${params.toString()}`), "_blank");
	};

	return (
		<div>
			<PageHeader
				title={t("orders")}
				action={
					<Link to="/orders/new" className={styles.newLink}>
						<Button>{t("newOrder")}</Button>
					</Link>
				}
			/>

			<div className={styles.filters}>
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "flex-start",
						gap: "var(--space-3)",
					}}
				>
					<FilterBar
						filters={[
							{
								key: "status",
								type: "select",
								label: t("all"),
								options: statusOptions.filter((o) => o.value !== ""),
							},
							{
								key: "search",
								type: "text",
								label: t("search"),
								placeholder: t("orderSearchPlaceholder"),
								width: 380,
							},
							{ key: "orderDate", type: "dateRange", label: t("orderDate") },
							...(isAdmin
								? [
										{
											key: "assignedTo",
											type: "select" as const,
											label: t("assignedTo"),
											options: (assignableUsers ?? []).map((u) => ({
												value: u.id,
												label: u.name,
											})),
										},
									]
								: []),
						]}
						values={{
							status,
							search: searchQuery,
							orderDateFrom,
							orderDateTo,
							assignedTo: assignedTo ?? "",
						}}
						onChange={(vals) => {
							navigate({
								to: ".",
								search: {
									status: vals.status || "",
									search: vals.search || "",
									orderDateFrom: vals.orderDateFrom || "",
									orderDateTo: vals.orderDateTo || "",
									assignedTo: vals.assignedTo || undefined,
									page: 1,
									size: pageSize,
								},
							});
						}}
					/>
					<Button
						variant="secondary"
						onClick={handleExport}
						style={{ marginLeft: "auto", flexShrink: 0 }}
					>
						<Download size={16} />
						{t("exportExcel")}
					</Button>
				</div>
			</div>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						columns={[
							{
								key: "documentNumber",
								header: t("documentNumber"),
								render: (row) => row.documentNumber || row.id.slice(0, 8),
							},
							{
								key: "company",
								header: t("company"),
								render: (row) => row.companyName || row.companyId || "-",
							},
							{
								key: "orderDate",
								header: t("orderDate"),
								render: (row) => (row.orderDate ? formatDate(row.orderDate) : "-"),
							},
							{
								key: "status",
								header: t("status"),
								render: (row) => {
									const s = row.status;
									const label = s === "cancelled" ? "İptal" : s === "closed" ? "Kapalı" : "Açık";
									const color =
										s === "cancelled" ? "#ef4444" : s === "closed" ? "#6b7280" : "#22c55e";
									return <span style={{ color, fontWeight: 600 }}>{label}</span>;
								},
							},
							{
								key: "total",
								header: t("total"),
								render: (row) => `${formatAmount(row.total)} ${row.currency || ""}`,
							},
							{
								key: "sap",
								header: "SAP",
								render: (row) => {
									if (row.sapDocNum || row.sapDocEntry)
										return <Badge variant="success">SAP #{row.sapDocNum ?? row.sapDocEntry}</Badge>;
									if (row.syncStatus === "in_progress")
										return <Badge variant="warning">{t("sapSyncing")}</Badge>;
									const isFailed = row.syncStatus === "failed";
									return (
										<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
											<span title={isFailed ? (row.syncError ?? undefined) : undefined}>
												<Badge variant={isFailed ? "danger" : "warning"}>
													{isFailed ? t("sapNotSent") : t("sapPending")}
												</Badge>
											</span>
											<Button
												size="sm"
												variant="secondary"
												disabled={retrySync.isPending && retrySync.variables === row.id}
												onClick={() =>
													retrySync.mutate(row.id, {
														onError: (e: unknown) =>
															alert(e instanceof Error ? e.message : t("sapRetryFailed")),
													})
												}
											>
												{t("sapRetry")}
											</Button>
										</div>
									);
								},
							},
							{
								key: "production",
								header: t("productionTracking"),
								render: (row) =>
									row.productionSummary ? (
										<button
											type="button"
											className={styles.productionLink}
											onClick={() => setPickerOrderId(row.id)}
										>
											{row.productionSummary.doneUnits}/{row.productionSummary.totalUnits}
										</button>
									) : (
										"-"
									),
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/orders/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={orders}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(newPage) =>
						navigate({ to: ".", search: { ...searchParams, page: newPage } })
					}
					onPageSizeChange={(newSize) =>
						navigate({ to: ".", search: { ...searchParams, page: 1, size: newSize } })
					}
				/>
			</Card>

			<ProductionUnitPickerModal
				orderId={pickerOrderId}
				onClose={() => setPickerOrderId(null)}
				onSelectSerial={(serialNo) => {
					setDetailOrderId(pickerOrderId);
					setDetailSerial(serialNo);
				}}
			/>
			<ProductionTrackingModal
				orderId={detailOrderId ?? ""}
				serialNo={detailSerial}
				onClose={() => setDetailSerial(null)}
			/>
		</div>
	);
}
